'use strict';

import {EventEmitter} from 'events';
import startsWith from 'lodash/startsWith';
import {IEventLogService} from '../event-log/event-log.service';

export default class TsVideoControlService extends EventEmitter {
    private document: Document;
    private window: Window;
    private eventLog: IEventLogService;

    private _isActive: boolean = false;
    public video: HTMLVideoElement | null = null;
    public videoReady: boolean = false;
    public options: any = {};
    public videoChangeLock: boolean = false;
    public nextVideo: any = null;
    public resetTimer: NodeJS.Timeout | null = null;
    public customTags: any[] = [];

    constructor(eventLogService: IEventLogService) {
        super();

        this.document = window.document;
        this.window = window;
        this.eventLog = eventLogService;

        this._addGlobalEventHandlers();
    }

    // No need to override emit for React - no digest cycle
    // Original had: emit(...args) { /* Angular $apply wrapper */ }

    setDimensions(width: number, height: number) {
        if (this.video) {
            this.video.width = width;
            this.video.height = height;
        }
    }

    setVideoElement(video: HTMLVideoElement, options: any) {
        // Cleanup old event handlers
        if (this.video) {
            this.video.pause();
            this.video.src = '';
            this.video.load();
        }

        this.options = options;
        this.video = video;
        this.video.crossOrigin = 'Anonymous';

        this.videoReady = false;
        this.emit('video:ready', false);

        // Replace jQuery with native event listeners
        this.video.addEventListener('error', () => {
            if (!this.video || !this.video.src) {
                return;
            }

            this.eventLog.videoDownloadFailed({
                url: this.video.src,
                error: 'Failed to load video from URL'
            });
            this.emit('video:failedtoloadurl');
        });

        // Start with a clean slate
        this.video.addEventListener('loadedmetadata', () => {
            if (this.video) {
                this.setDimensions(this.video.videoWidth, this.video.videoHeight);
                this.videoReady = true;
                this.emit('video:ready', true);

                setTimeout(() => this._handleSizing(), 0);
            }
        });

        // If provided with default dimensions, we use these
        if (this.options.width && this.options.height) {
            this.setDimensions(this.options.width, this.options.height);
        }
    }

    /*
     * Sets the video of the current element and
     * also resize control according to video aspect ratio
     *
     * @param video {objectURL} new video
     */
    setVideo(video: any) {
        this.videoReady = false;

        if (this.video && video.url) {
            this.video.poster = video.url;
        }

        if (this.videoChangeLock) {
            this.nextVideo = video;
        } else {
            this.videoChangeLock = true;
            this._syncSetVideo(video.video);
        }
    }

    /**
     * Sets the background video, and checks at the end if a new background
     * was specified while the current one was set. In this case it recurse
     * and sets the last specified one (if multiple backgrounds were specified
     * while locked, only the last of them is set).
     *
     * @param url {objectURL} new background video
     */
    _syncSetVideo(url: string) {
        if (this.video && url) {
            // Append timestamp parameter to URL to prevent browser form using a cached video, see:
            // https://stackoverflow.com/questions/14228839/all-of-my-browsers-are-not-sending-the-origin-header.
            // Signed S3 urls can't have extra params, so they are excluded from this
            if ((startsWith(url, 'http://') || startsWith(url, 'https://')) && !/X-Amz-Signature/.test(url)) {
                this.video.src = `${url}?timestamp=${Date.now()}`;
            } else {
                this.video.src = url;
            }

            if (this.nextVideo) {
                const nextVideo = this.nextVideo;

                this.nextVideo = null;
                this._syncSetVideo(nextVideo.video);
            } else {
                this.videoChangeLock = false;
            }
        }
    }

    setContainerScale(scale?: number) {
        if (!this.video) return;

        const container = this.video.parentElement;
        if (!container) return;

        const transform = scale || scale === 1 ? `scale(${scale})` : 'none';
        const maxHeight = `${100 / (scale || 1)}%`;

        // Replace jQuery with native style manipulation
        container.style.transform = transform;
        container.style.maxHeight = maxHeight;
    }

    _handleSizing() {
        if (!this.video) return;

        const container = this.video.parentElement;
        const outerContainer = container && container.parentElement;

        if (!outerContainer) {
            return;
        }

        // Replace jQuery with native DOM properties
        this.options.maxWidth = outerContainer.clientWidth;
        this.options.maxHeight = outerContainer.clientHeight;

        const sx = this.options.maxWidth / this.video.width;
        const sy = this.options.maxHeight / this.video.height;
        const sc = Math.min(sx, sy);

        this.setContainerScale(sc);
    }

    _addGlobalEventHandlers() {
        const resizeRotateHandler = () => {
            if (!this.video) {
                return;
            }

            // Replace Angular $timeout with native setTimeout
            if (this.resetTimer) {
                clearTimeout(this.resetTimer);
            }
            this.resetTimer = setTimeout(() => this._handleSizing(), 150);
        };

        // Replace Angular event listeners with native ones
        this.window.addEventListener('resize', resizeRotateHandler);
        this.window.addEventListener('orientationchange', resizeRotateHandler);
    }

    getTags() {
        return this.customTags || [];
    }

    setTags(tags: any[]) {
        this.customTags = tags;
    }

    get isActive() {
        return this._isActive;
    }

    set isActive(isActive: boolean) {
        if (!isActive) {
            this.videoReady = false;
            this.emit('video:ready', false);
        }

        this._isActive = isActive || false;

        this.emit('video:isActive', this._isActive);
    }

    get videoUrl() {
        return this.video?.src || null;
    }

    get videoElement() {
        return this.video;
    }

    // Add dispose method for cleanup (not in original but good practice)
    dispose() {
        if (this.resetTimer) {
            clearTimeout(this.resetTimer);
        }

        if (this.video) {
            this.video.pause();
            this.video.src = '';
            this.video.load();
        }

        this.removeAllListeners();
    }
}
